import { ChangeDetectorRef, Directive } from '@angular/core';

import { Observable, Observer, Subject } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import * as papaparse from 'papaparse';
import * as encodingDetect from 'encoding-japanese';
import * as iconvLite from 'iconv-lite';

import { allowedFileTypes } from '../../../Forms/models/file-upload.model';
import { ConfirmDialogComponent } from '../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../Dialogs/models/confirm-dialog.model';
import { CsvImportDialogService, ImportedFile } from '../../../Dialogs/components/CsvImportDialog/csv-import-dialog.service';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';


@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class CsvImporterComponent<T, Y>  {

    csvFiles: ImportedFile<T, Y>[] = [];
    selectedOptions: DropdownOptionModel[][] = [];
    csvColumnDropdownOptions: DropdownOptionModel[][] = [];

    readonly csvMimeFileTypesRegex = allowedFileTypes.csvMIMETypesRegex;

    constructor(
        protected csvImportDialogService: CsvImportDialogService<T, Y>,
        protected coreDialogService: CoreDialogService,
        protected messageService: MessageService,
        protected localizationService: LocalizationService,
        protected cd: ChangeDetectorRef
    ) {}

    abstract showConfirmImportDialog(): void;

    protected readFilesToCsvDialogService(
        files: File[] | FileList,
        isInitialRead: boolean
    ): Observable<File[]> {
        const filesToProcess: Observable<File>[] = [];

        for (let i = 0; i < files.length; i++) {
            filesToProcess.push(this.readFileToCsvDialogService(files[i]));
        }

        const fileUploadProvider = new Subject<File[]>();
        const successfulFiles: File[] = [];
        const failedFileNames: string[] = [];

        for (const fileObservable of filesToProcess) {
            fileObservable.pipe(
                finalize(() => {
                    // Complete function only used to create inital file list
                    if (!isInitialRead) {
                        return;
                    }

                    if (successfulFiles.length + failedFileNames.length === filesToProcess.length) {
                        fileUploadProvider.next(successfulFiles);
                        if (failedFileNames.length > 0) {
                            this.showFailedFilesDialog(failedFileNames);
                        }
                    }
                })
            ).subscribe({
                next: (successFile) => successfulFiles.push(successFile),
                error: (errorFileName) => failedFileNames.push(errorFileName)
            });
        }

        return fileUploadProvider;
    }

    protected readFileToCsvDialogService(file: File): Observable<File> {
        return new Observable((observer: Observer<File>) => {
            if (!this.csvMimeFileTypesRegex.includes(file.type)) {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    detail: this.localizationService.transform('Discover.contactImporter.errors.incorrectFileType')
                });
                observer.error(file.name);
                return;
            }

            // Both contact and mention importer extends this component, and in contact importer center panel, user can upload a file from an input click
            // instead of from FileUploadComponent, so file encoding needs to be validated in this case
            this.validateFileEncoding(file).subscribe(isValidFile => {
                if (!isValidFile) {
                    observer.error(file.name);
                    return;
                }

                const reader = new FileReader();

                reader.readAsArrayBuffer(file);
                reader.onload = (loadEvent: any) => {
                    let byteArray = new Uint8Array(loadEvent.target.result as ArrayBuffer);
                    let fileEncoding = encodingDetect.detect(byteArray);

                    if (fileEncoding === 'UNICODE') {
                        // Handle "form feed" control character, which encodingDetect doesn't think is UTF-8
                        byteArray = byteArray.map((a) => a === 0x0C ? 0x0A : a);
                        fileEncoding = encodingDetect.detect(byteArray);
                    }

                    const contentString = iconvLite.decode(Buffer.from(byteArray), fileEncoding);
                    papaparse.parse<string[]>(contentString, {
                        skipEmptyLines: 'greedy',
                        header: false,
                        complete: (results) => {
                            this.csvImportDialogService.processData(file.name, results.data);
                            observer.next(file);
                            observer.complete();
                        }
                    });
                };
            });
        });
    }

    protected validateFileEncoding(file: File): Observable<boolean> {
        return new Observable(observer => {
            const reader = new FileReader();
            reader.readAsArrayBuffer(file);
            reader.onload = (loadEvent: any) => {
                let byteArray = new Uint8Array(loadEvent.target.result as ArrayBuffer);
                let fileEncoding = encodingDetect.detect(byteArray);

                if (fileEncoding === 'UNICODE') {
                    // Handle "form feed" control character, which encodingDetect doesn't think is UTF-8
                    byteArray = byteArray.map((a) => a === 0x0C ? 0x0A : a);
                    fileEncoding = encodingDetect.detect(byteArray);
                }

                // ASCII may be a little broad but it covers most csv formats that aren't UTF8 (Win-1252, iso-8859, etc)
                if (fileEncoding !== 'UTF8' && fileEncoding !== 'ASCII') {
                    observer.next(false);
                    observer.complete();
                    return;
                }
                observer.next(true);
                observer.complete();
            };
        });
    }

    protected showFailedFilesDialog(failedFileNames: string[]) {
        const io: ConfirmDialogModel = {
            inputs: {
                id: 'csvImporter.invalidEncodingConfirmDialog',
                alertType: messageSeverityEnum.error,
                heading: 'Discover.csvImporter.errors.invalidEncodingDialog.header',
                message: 'Discover.csvImporter.errors.invalidEncodingDialog.message',
                alertMessage: this.localizationService.transform(
                    'Discover.csvImporter.errors.invalidEncodingDialog.alertMessage',
                    { files: failedFileNames.join(', ') }
                ),
                okLabel: 'Discover.csvImporter.errors.invalidEncodingDialog.acknowledge'
            },
            outputs: {
                dialogClose: () => {}
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    // This is called when the selected files have been processed
    protected updateDropdownOptions() {
        this.selectedOptions = [];
        this.csvColumnDropdownOptions = [];
        if (this.csvFiles && this.csvFiles.length > 0) {
            let fileIndex = 0;
            this.csvFiles.forEach(file => {
                this.csvColumnDropdownOptions[fileIndex] = [];
                if (file.csvColumns && file.csvColumns.length > 0) {
                    file.csvColumns.forEach(column => {
                        this.csvColumnDropdownOptions[fileIndex].push({
                            label: column.columnLabel,
                            value: column
                        });
                    });
                }

                this.selectedOptions[fileIndex] = [];
                if (file.csvFields && file.csvFields.length > 0) {
                    for (let i = 0; i < file.csvFields.length; i++) {
                        this.selectedOptions[fileIndex][i] = this.csvColumnDropdownOptions[fileIndex]
                            .find(option => file.csvFields[i].targetColumn === option.value);
                    }
                }
                fileIndex++;
            });
        }
        this.cd.detectChanges();
    }
}
